(ns io.daskul.tutorial.webapp.views.today.vm
  "Pure view-model logic for the Today home (SMS-144) — the admin command
   center (feature 0013 §Surfaces → \"Today (home)\").

   Carries NO UI dependency (no `io.daskul.webapp.ui` / icon import) so it runs
   under the webapp-ui :node-test target — the JSX render lives in
   `views/today.cljs`. Mirrors the SMS-148 lead-detail split: pure
   number-crunching / chip-mapping logic is node-tested, the visual layout is
   e2e-verified (frontend-component-architecture — separate logic from layout).

   Responsibilities:
   - `greeting`      — time-of-day greeting (clock-free; caller passes the hour).
   - `funnel-cards`  — fold a leads counts-by-state map into the 4 funnel cards
                       (New / In progress / Trial / Enrolled) the row renders.
   - `cap-list`      — cap a list at N and report the true total + overflow."
  (:require
   [clojure.string :as str]
   [io.daskul.webapp.hooks.list-params :as list-params]))

;; ============================================================================
;; Greeting (0013 §Today — \"Good morning, <name>\")
;; ============================================================================

(defn greeting
  "Time-of-day greeting for `name`, given the local `hour` (0–23). Clock-free:
   the caller reads the wall clock so this stays unit-testable. Falls back to a
   name-less greeting when `name` is blank."
  [name hour]
  (let [part (cond
               (< hour 12) "Good morning"
               (< hour 18) "Good afternoon"
               :else       "Good evening")
        nm (when (and name (not (str/blank? (str name)))) (str/trim (str name)))]
    (if nm (str part ", " nm) part)))

;; ============================================================================
;; Funnel cards (0013 §Today — 4 stage-count cards)
;; ============================================================================
;;
;; The funnel collapses the lead FSM's fine-grained states into the four
;; business-facing stages on the card row. \"Trial\" rolls up the three trial
;; states (scheduled / no-show / attended); \"In progress\" is `contacted`.
;; `lost` is intentionally excluded — the funnel tracks live pipeline, not
;; churn. Each card's chip color comes from 0013 §Design tokens
;; (New=amber, In progress=gray, Trial=violet, Enrolled=green).

;; #324: the :states vectors that used to live here had already drifted from
;; the spec — "In progress" was ["contacted"] only, and "Lost" was missing.
;; Each card now deep-links to its stage (?segment=<key>), and a naive link
;; would have spelled out ?state=trial-scheduled,no-show,trial-attended — a
;; third copy of the grouping, in URL-construction code. Labels, member
;; states, and counts now all come from the Lead aggregate's own segment
;; config; only the chip colour stays here, as a Today presentation choice.

(def funnel-stages
  "The stages the funnel row shows, in order, with their chip colour. `all`
   and `lost` are deliberately absent — the funnel tracks live pipeline, not
   totals or churn. Keys are Lead segment keys."
  [{:key "new"         :color "yellow"}
   {:key "in-progress" :color "gray"}
   {:key "trial"       :color "brand"}
   {:key "enrolled"    :color "green"}])

(defn state-segments
  "The Lead aggregate's :state segment definitions, out of its frontend config.
   Empty when config hasn't loaded yet, so callers render nothing rather than
   guessing at stage names."
  [lead-config]
  (or (some (fn [f] (when (= :state (:field f)) (:segments f)))
            (get-in lead-config [:list :filters]))
      []))

(defn funnel-cards
  "Fold a leads counts-by-state map into the ordered funnel cards
   [{:key :label :color :count} ...], taking each card's label and the states
   it sums from `segments`.

   nil counts (not yet loaded) yield 0s so the row keeps its shape. A stage the
   aggregate no longer declares is dropped rather than rendered with a dead
   deep-link."
  [counts segments]
  (let [by-key (into {} (map (juxt :key identity)) segments)]
    (into []
          (keep (fn [{:keys [key color]}]
                  (when-let [seg (get by-key key)]
                    {:key key
                     :label (:label seg)
                     :color color
                     :count (or (list-params/segment-count counts seg) 0)})))
          funnel-stages)))

;; ============================================================================
;; List capping (0013 §Today — \"each list caps at ~5 … View all N →\")
;; ============================================================================

(defn cap-list
  "Cap `items` at `n` for display. Returns {:rows :total :overflow?} where
   :rows is the first n items, :total the true count (drives the header badge),
   and :overflow? whether a \"View all N →\" footer is warranted."
  [items n]
  (let [items (vec items)
        total (count items)]
    {:rows (vec (take n items))
     :total total
     :overflow? (> total n)}))

;; ============================================================================
;; Trials-today rows (0013 §Today — \"Trials today\" check-in list)
;; ============================================================================

(defn trial-time
  "Render a SessionAttendee's parent-Session start as a short local time
   ('5:00 PM') from the included :session's naive :start-time ('HH:MM:SS'), or
   nil. #590: the value is already the school's wall clock, so no tz is applied
   — the `tz` arg is kept for call-site compatibility, unused."
  [sa _tz]
  (let [t (get-in sa [:session :start-time])]
    (when t
      (let [[h mm] (mapv #(js/parseInt % 10) (str/split t #":"))
            ampm (if (>= h 12) "PM" "AM")
            h12 (let [x (mod h 12)] (if (zero? x) 12 x))]
        (str h12 ":" (when (< mm 10) "0") mm " " ampm)))))

(defn trial-row
  "Shape one trial SessionAttendee (+ included :session) into a flat row
   view-model the Trials-today list renders: the child's name (from the SA
   after-read :attendee-name), the trial time, and the SA id (for the Check-in
   link)."
  [sa tz]
  {:id (:id sa)
   :name (or (:attendee-name sa) "Unknown")
   :time (trial-time sa tz)})

;; ============================================================================
;; Unlinked-guardian card (#859 — orphaned guardians)
;; ============================================================================

(defn orphan-card-message
  "Body line for the unlinked-guardians card, or nil when `count` is not
   positive. Returning nil is the single source of the card's hide-when-0
   behavior (#859 AC: quiet when there's nothing to action) — the card renders
   iff this is non-nil — and keeps the singular/plural wording node-testable."
  [count]
  (when (and (number? count) (pos? count))
    (str count (if (= 1 count) " guardian isn't" " guardians aren't")
         " linked to a student.")))
